'use client';

/**
 * useTerminology Hook
 *
 * React hook for white-label terminology customization.
 * Allows businesses to customize entity names, field labels, and page titles.
 *
 * Works in conjunction with useI18n - provides business-specific overrides
 * that take precedence over standard i18n translations.
 *
 * Part of Issue #232 - White-Label Terminology System.
 *
 * @example
 * ```tsx
 * import { useTerminology } from '@ops-matrix/design-system-ui';
 *
 * function LoanLabel() {
 *   const { term } = useTerminology();
 *   // Returns "Advance" if business has override, otherwise "Loan"
 *   return <span>{term('entities.loan')}</span>;
 * }
 *
 * function DynamicLabel() {
 *   const { term, hasOverride } = useTerminology();
 *   return (
 *     <span>
 *       {term('entities.consumer')}
 *       {hasOverride('entities.consumer') && <Badge>Custom</Badge>}
 *     </span>
 *   );
 * }
 * ```
 */

import { createContext, useCallback, useContext, useMemo } from 'react';
import { useI18n } from './useI18n';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Terminology overrides map - key is i18n translation key, value is custom term
 */
export type TerminologyOverrides = Record<string, string>;

/**
 * Available terminology key with metadata
 */
export interface TerminologyKeyInfo {
  /** Translation key (e.g., "entities.loan") */
  key: string;
  /** Default English value */
  defaultValue: string;
  /** Category for grouping in settings UI */
  category: string;
  /** Description of what this key represents */
  description: string;
}

/**
 * Terminology context value provided by TerminologyProvider
 */
export interface TerminologyContextValue {
  /** Current terminology overrides for the business */
  overrides: TerminologyOverrides;
  /** Whether terminology is loading */
  isLoading: boolean;
  /** Whether business has any custom terminology */
  hasCustomTerminology: boolean;
  /**
   * Get term - checks overrides first, then falls back to i18n
   * @param key - Translation key (e.g., "entities.loan")
   * @param params - Optional interpolation params
   */
  term: (key: string, params?: Record<string, string | number>) => string;
  /** Check if a specific key has an override */
  hasOverride: (key: string) => boolean;
  /** Get the override value for a key (undefined if no override) */
  getOverride: (key: string) => string | undefined;
  /** Available keys that can be overridden */
  availableKeys: TerminologyKeyInfo[];
}

// ============================================================================
// AVAILABLE TERMINOLOGY KEYS
// ============================================================================

/**
 * Keys that can be overridden by businesses
 * These are the most commonly customized terms in white-label scenarios
 */
export const AVAILABLE_TERMINOLOGY_KEYS: TerminologyKeyInfo[] = [
  // Entities
  { key: 'entities.loan', defaultValue: 'Loan', category: 'entities', description: 'Loan/Advance/Financing' },
  { key: 'entities.loans', defaultValue: 'Loans', category: 'entities', description: 'Loans (plural)' },
  { key: 'entities.case', defaultValue: 'Case', category: 'entities', description: 'Case/File/Application' },
  { key: 'entities.cases', defaultValue: 'Cases', category: 'entities', description: 'Cases (plural)' },
  { key: 'entities.consumer', defaultValue: 'Consumer', category: 'entities', description: 'Consumer/Client/Borrower' },
  { key: 'entities.consumers', defaultValue: 'Consumers', category: 'entities', description: 'Consumers (plural)' },
  { key: 'entities.partner', defaultValue: 'Partner', category: 'entities', description: 'Partner/Affiliate/Broker' },
  { key: 'entities.partners', defaultValue: 'Partners', category: 'entities', description: 'Partners (plural)' },
  { key: 'entities.document', defaultValue: 'Document', category: 'entities', description: 'Document/File' },
  { key: 'entities.documents', defaultValue: 'Documents', category: 'entities', description: 'Documents (plural)' },
  {
    key: 'entities.property',
    defaultValue: 'Property',
    category: 'entities',
    description: 'Property/Asset/Collateral',
  },
  { key: 'entities.properties', defaultValue: 'Properties', category: 'entities', description: 'Properties (plural)' },
  { key: 'entities.team', defaultValue: 'Team', category: 'entities', description: 'Team/Staff/Members' },
  { key: 'entities.branch', defaultValue: 'Branch', category: 'entities', description: 'Branch/Office/Location' },
  { key: 'entities.branches', defaultValue: 'Branches', category: 'entities', description: 'Branches (plural)' },

  { key: 'entities.lead', defaultValue: 'Lead', category: 'entities', description: 'Lead/Prospect/Inquiry' },
  { key: 'entities.leads', defaultValue: 'Leads', category: 'entities', description: 'Leads (plural)' },

  // Navigation
  {
    key: 'navigation.platformName',
    defaultValue: 'Platform',
    category: 'navigation',
    description: 'Platform name shown in tours and welcome messages',
  },
  {
    key: 'navigation.dashboard',
    defaultValue: 'Dashboard',
    category: 'navigation',
    description: 'Dashboard page title',
  },
  { key: 'navigation.cases', defaultValue: 'Cases', category: 'navigation', description: 'Cases navigation item' },
  {
    key: 'navigation.consumers',
    defaultValue: 'Consumers',
    category: 'navigation',
    description: 'Consumers navigation item',
  },
  {
    key: 'navigation.partners',
    defaultValue: 'Partners',
    category: 'navigation',
    description: 'Partners navigation item',
  },
  {
    key: 'navigation.documents',
    defaultValue: 'Documents',
    category: 'navigation',
    description: 'Documents navigation item',
  },
  {
    key: 'navigation.analytics',
    defaultValue: 'Analytics',
    category: 'navigation',
    description: 'Analytics navigation item',
  },
  {
    key: 'navigation.settings',
    defaultValue: 'Settings',
    category: 'navigation',
    description: 'Settings navigation item',
  },

  // Status labels
  { key: 'status.active', defaultValue: 'Active', category: 'status', description: 'Active status label' },
  { key: 'status.pending', defaultValue: 'Pending', category: 'status', description: 'Pending status label' },
  { key: 'status.approved', defaultValue: 'Approved', category: 'status', description: 'Approved status label' },
  { key: 'status.rejected', defaultValue: 'Rejected', category: 'status', description: 'Rejected status label' },
  { key: 'status.completed', defaultValue: 'Completed', category: 'status', description: 'Completed status label' },

  // Section titles
  { key: 'section.overview', defaultValue: 'Overview', category: 'section', description: 'Overview section title' },
  { key: 'section.details', defaultValue: 'Details', category: 'section', description: 'Details section title' },
  { key: 'section.history', defaultValue: 'History', category: 'section', description: 'History section title' },
  { key: 'section.activity', defaultValue: 'Activity', category: 'section', description: 'Activity section title' },
];

// ============================================================================
// CONTEXT
// ============================================================================

const TerminologyContext = createContext<TerminologyContextValue | null>(null);

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access terminology utilities
 *
 * Must be used within TerminologyProvider (or DesignSystemProvider which includes it)
 * If not within a provider, falls back to standard i18n translations
 */
export function useTerminology(): TerminologyContextValue {
  const context = useContext(TerminologyContext);
  const { t } = useI18n();

  // Create a fallback context that uses i18n directly
  const fallbackContext = useMemo<TerminologyContextValue>(
    () => ({
      overrides: {},
      isLoading: false,
      hasCustomTerminology: false,
      term: (key: string, params?: Record<string, string | number>) =>
        params ? t(key as Parameters<typeof t>[0], params) : t(key as Parameters<typeof t>[0]),
      hasOverride: () => false,
      getOverride: () => undefined,
      availableKeys: AVAILABLE_TERMINOLOGY_KEYS,
    }),
    [t],
  );

  return context ?? fallbackContext;
}

// ============================================================================
// PROVIDER VALUE HOOK
// ============================================================================

interface UseTerminologyValueOptions {
  /** Terminology overrides from API */
  overrides?: TerminologyOverrides;
  /** Whether data is loading */
  isLoading?: boolean;
}

/**
 * Hook to create Terminology context value
 * Used internally by TerminologyProvider
 *
 * @example
 * ```tsx
 * // In portal's provider setup
 * function TerminologyProvider({ children }) {
 *   const { data, isLoading } = trpc.business.terminology.get.useQuery();
 *
 *   const terminologyValue = useTerminologyValue({
 *     overrides: data?.overrides ?? {},
 *     isLoading,
 *   });
 *
 *   return (
 *     <TerminologyContext.Provider value={terminologyValue}>
 *       {children}
 *     </TerminologyContext.Provider>
 *   );
 * }
 * ```
 */
export function useTerminologyValue(options: UseTerminologyValueOptions = {}): TerminologyContextValue {
  const { overrides = {}, isLoading = false } = options;
  const { t } = useI18n();

  const hasCustomTerminology = Object.keys(overrides).length > 0;

  /**
   * Get term with override support
   * Checks overrides first, then falls back to i18n
   */
  const term = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      // Check if there's an override for this key
      if (key in overrides) {
        const overrideValue = overrides[key];
        // Simple interpolation for override values
        if (params && overrideValue) {
          return Object.entries(params).reduce(
            (str, [param, value]) => str.replaceAll(`{{${param}}}`, String(value)),
            overrideValue,
          );
        }
        return overrideValue;
      }
      // Fall back to i18n translation
      return params ? t(key as Parameters<typeof t>[0], params) : t(key as Parameters<typeof t>[0]);
    },
    [overrides, t],
  );

  const hasOverride = useCallback((key: string): boolean => key in overrides, [overrides]);

  const getOverride = useCallback((key: string): string | undefined => overrides[key], [overrides]);

  return useMemo(
    () => ({
      overrides,
      isLoading,
      hasCustomTerminology,
      term,
      hasOverride,
      getOverride,
      availableKeys: AVAILABLE_TERMINOLOGY_KEYS,
    }),
    [overrides, isLoading, hasCustomTerminology, term, hasOverride, getOverride],
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { TerminologyContext };

/**
 * Get available terminology keys for settings UI
 */
export function getAvailableTerminologyKeys(): TerminologyKeyInfo[] {
  return AVAILABLE_TERMINOLOGY_KEYS;
}

/**
 * Get terminology keys by category
 */
export function getTerminologyKeysByCategory(category: string): TerminologyKeyInfo[] {
  return AVAILABLE_TERMINOLOGY_KEYS.filter((k) => k.category === category);
}

/**
 * Get unique categories from available keys
 */
export function getTerminologyCategories(): string[] {
  return [...new Set(AVAILABLE_TERMINOLOGY_KEYS.map((k) => k.category))];
}
